import { Controller } from "@hotwired/stimulus"

// Per-card controller for the Decide lane (docs/design/Inbox.dc.html). A Decide
// card carries its own context drawer and add-to-campaign modal inline; this
// toggles them. Clicking the card body opens the drawer; the "Add to campaign"
// exit (on the card or in the drawer) opens the modal and must not also open the
// drawer, so openModal stops propagation.
export default class extends Controller {
  static targets = ["drawer", "modal", "composer", "noteInput", "noteCount"]

  // If the card is turbo-removed while its drawer/modal was open (e.g. dismissed
  // from the drawer), release the scroll lock this controller set on the body.
  disconnect() {
    document.body.classList.remove("overflow-hidden")
  }

  openDrawer() {
    if (this.hasDrawerTarget) {
      this.drawerTarget.classList.remove("hidden")
      document.body.classList.add("overflow-hidden")
    }
  }

  closeDrawer(event) {
    if (event) event.preventDefault()
    if (this.hasDrawerTarget) this.drawerTarget.classList.add("hidden")
    document.body.classList.remove("overflow-hidden")
  }

  openModal(event) {
    // Fired from a button inside the card / drawer — keep it from bubbling to the
    // card's openDrawer, and close the drawer if it was the launch point.
    if (event) {
      event.preventDefault()
      event.stopPropagation()
    }
    if (this.hasDrawerTarget) this.drawerTarget.classList.add("hidden")
    if (this.hasModalTarget) {
      this.modalTarget.classList.remove("hidden")
      document.body.classList.add("overflow-hidden")
    }
  }

  closeModal(event) {
    if (event) event.preventDefault()
    if (this.hasModalTarget) this.modalTarget.classList.add("hidden")
    document.body.classList.remove("overflow-hidden")
  }

  // The connect-with-a-note composer. Same open/close contract as the modal:
  // fired from a button inside the card or drawer, so it must not also open the
  // drawer, and it closes the drawer if that was the launch point.
  openComposer(event) {
    if (event) {
      event.preventDefault()
      event.stopPropagation()
    }
    if (this.hasDrawerTarget) this.drawerTarget.classList.add("hidden")
    if (this.hasComposerTarget) {
      this.composerTarget.classList.remove("hidden")
      document.body.classList.add("overflow-hidden")
      this.countNote()
      // The box opens EMPTY and focused — no pre-fill, no draft to delete first.
      if (this.hasNoteInputTarget) this.noteInputTarget.focus()
    }
  }

  closeComposer(event) {
    if (event) event.preventDefault()
    if (this.hasComposerTarget) this.composerTarget.classList.add("hidden")
    document.body.classList.remove("overflow-hidden")
  }

  // Live character count against LinkedIn's note limit. The textarea's maxlength
  // is what actually stops the typing; this is what makes the ceiling visible
  // before the user hits it, and it turns amber as they approach it.
  countNote() {
    if (!this.hasNoteInputTarget || !this.hasNoteCountTarget) return

    const limit = parseInt(this.noteInputTarget.dataset.decideCardLimitValue, 10) || 300
    const used = this.noteInputTarget.value.length

    this.noteCountTarget.textContent = `${used} / ${limit}`
    this.noteCountTarget.style.color = used >= limit ? "#C4495C" : used > limit * 0.9 ? "#A88B2E" : "#9094AD"
  }

  // Swallow clicks inside the modal panel so they don't reach the backdrop's
  // close handler.
  stop(event) {
    event.stopPropagation()
  }
}
